import React, { useEffect, useRef } from 'react';
import style from './menuCard.module.css';
import { Menu, MenuItem, MenuTypes } from '@app/types';
import MenuItemCard from './MenuItemCard';
import { useRestaurantContext } from '@app/pages/Restaurant/context';
import { Colors } from '@app/config/colors';
import DIMENSIONS from '@app/config/dimensions';
import { MenuItemFavorited } from '@app/types/menu-item';

type Props = {
  menu: Menu;
  index: number;
  onAddMenuItemClick: (menuItem: MenuItem | MenuItemFavorited, menu: Menu) => void;
  onRechedTopOfScroll: (menu: Menu, index: number) => void;
};

function MenuCard({ menu, index, onAddMenuItemClick, onRechedTopOfScroll }: Props) {
  const { primaryColor } = useRestaurantContext();

  const containerRef = useRef<HTMLDivElement>(null);

  function handleAddClick(menuItem: MenuItem | MenuItemFavorited) {
    onAddMenuItemClick(menuItem, menu);
  }

  useEffect(() => {
    function handleScroll() {
      const rect = containerRef.current?.getBoundingClientRect();

      const scrollYDiscountedAppBarHeight =
        document.body.scrollTop + DIMENSIONS.APP_BAR.HEIGHT + DIMENSIONS.MAIN_PAGE.SEARCH_AND_SCROLLER_BOX_HEIGHT;

      const menuCardInitialYPosition = rect?.y ?? 0;
      const menuCardFinalYPosition = menuCardInitialYPosition + (rect?.height ?? 0);

      if (
        scrollYDiscountedAppBarHeight >= menuCardInitialYPosition &&
        scrollYDiscountedAppBarHeight <= menuCardFinalYPosition
      )
        onRechedTopOfScroll(menu, index);
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [onRechedTopOfScroll]);

  return (
    <div ref={containerRef} className={style.container} id={`menu-card-${menu.id}`}>
      <div className={style.sectionHeaderMenu}>
        <h3 style={{ color: primaryColor || Colors.DefaultPrimaryColor }}>{menu.nome}</h3>
      </div>
      {menu.itens.map((item) => {
        return (
          <MenuItemCard
            key={item.id}
            menuItem={item}
            onAddClick={handleAddClick}
            isPizzaItem={menu.tipoMenu === MenuTypes.Pizza}
          />
        );
      })}
    </div>
  );
}

export default React.memo(MenuCard, (prevProps, nextProps) => {
  return (
    prevProps.menu.id === nextProps.menu.id &&
    prevProps.index === nextProps.index &&
    prevProps.menu.itens === nextProps.menu.itens &&
    prevProps.onAddMenuItemClick === nextProps.onAddMenuItemClick &&
    prevProps.onRechedTopOfScroll === nextProps.onRechedTopOfScroll
  );
});
